import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../hook";
import { RootState } from "../store";
import { DashboardData, DashboardFilter, OnboardingData, DREItem } from "../../types/dashboard";

export type DashboardState = {
  data: DashboardData | null;
  filters: DashboardFilter;
  loading: boolean;
  error: string | null;
  expandedDREItems: string[];
  onboarding: OnboardingData;
};

const initialState: DashboardState = {
  data: null,
  filters: {},
  loading: false,
  error: null,
  expandedDREItems: [],
  onboarding: {
    hasSeenOnboarding: false,
    numFiliais: 1,
    filiaisNames: ["", "", ""],
  },
};

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setDashboardData: (state, action: PayloadAction<DashboardData>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<DashboardFilter>) => {
      state.filters = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    toggleDREItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const index = state.expandedDREItems.indexOf(itemId);
      if (index > -1) {
        state.expandedDREItems.splice(index, 1);
      } else {
        state.expandedDREItems.push(itemId);
      }
    },
    setOnboardingData: (state, action: PayloadAction<Partial<OnboardingData>>) => {
      state.onboarding = { ...state.onboarding, ...action.payload };
    },
    completeOnboarding: (state) => {
      state.onboarding.hasSeenOnboarding = true;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
  },
});

export const {
  setDashboardData,
  setFilters,
  setLoading,
  setError,
  toggleDREItem,
  setOnboardingData,
  completeOnboarding,
  clearFilters,
} = dashboardSlice.actions;

// Selectors
export const getDashboardData = (state: RootState) => state.dashboard.data;
export const getDashboardFilters = (state: RootState) => state.dashboard.filters;
export const getDashboardLoading = (state: RootState) => state.dashboard.loading;
export const getDashboardError = (state: RootState) => state.dashboard.error;
export const getExpandedDREItems = (state: RootState) => state.dashboard.expandedDREItems;
export const getOnboardingData = (state: RootState) => state.dashboard.onboarding;

// Hooks
export const useDashboardData = () => useAppSelector(getDashboardData);
export const useDashboardFilters = () => useAppSelector(getDashboardFilters);
export const useDashboardLoading = () => useAppSelector(getDashboardLoading);
export const useDashboardError = () => useAppSelector(getDashboardError);
export const useExpandedDREItems = () => useAppSelector(getExpandedDREItems);
export const useOnboardingData = () => useAppSelector(getOnboardingData);

export default dashboardSlice.reducer;
